

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@sunfish/ui/components/form";
import { Input, type InputProps } from "@sunfish/ui/components/input";
import { clsx } from "clsx";
import { type ReactNode, useState } from "react";
import type { Control, FieldValues, Path } from "react-hook-form";
import { cn } from "@sunfish/lib/utils";
import { IconBlock } from "@sunfish/ui/components/icon-block";
import { icons } from "lucide-react";
import { RichTextDescription } from "./rich-text-description";

export type InputBlockFieldTypes =
  | "text"
  | "number"
  | "email"
  | "password"
  | "url"
  | "tel";

const defaultFieldProps = {
  autoComplete: "off",
  autoFocus: false,
};

export function InputBlock<T extends FieldValues>({
  control,
  name,
  label,
  description,
  required,
  placeholder,
  type: _type = "text",
  disabled,
  fieldProps: _fieldProps = defaultFieldProps,
  prependIcon,
  tabIndex,
}: {
  control: Control<T, unknown>;
  name: Path<T>;
  label?: string | null;
  description?: string | null | ReactNode;
  required?: boolean;
  placeholder?: string | null;
  type?: InputBlockFieldTypes;
  disabled?: boolean;
  fieldProps?: Partial<InputProps>;
  prependIcon?: ReactNode;
  tabIndex?: number;
}) {
  const fieldProps = { ...defaultFieldProps, ..._fieldProps };

  const [type, setType] = useState<InputBlockFieldTypes>(_type ?? "text");

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <div className={"flex flex-wrap justify-between items-center"}>
            {!!label?.length && (
              <FormLabel
                className={clsx("flex flex-wrap gap-1", {
                  "pointer-events-none": disabled,
                })}
              >
                <span>{label}</span>
                {required && (
                  <span className={"align-super text-destructive"}>*</span>
                )}
              </FormLabel>
            )}
            <FormMessage
              className={"text-xs leading-none max-w-[250px] text-right"}
            />
          </div>
          {description && (
            <FormDescription>
              <RichTextDescription content={description} />
            </FormDescription>
          )}
          <FormControl className={clsx({ "opacity-50": disabled })}>
            <div
              className={cn("relative", { "pointer-events-none": disabled })}
            >
              <Input
                type={type}
                disabled={disabled}
                {...field}
                onChange={(val) =>
                  field.onChange(
                    type === "number"
                      ? Number(val.target.value)
                      : val.target.value,
                  )
                }
                placeholder={placeholder ?? ""}
                className={cn(
                  fieldProps?.className ?? "",
                  type === "password" ? "pe-8" : "",
                  prependIcon ? "ps-10" : "",
                )}
                tabIndex={tabIndex}
                {...fieldProps}
              />
              {_type === "password" && (
                <button
                  onClick={() =>
                    setType(type === "password" ? "text" : "password")
                  }
                  type={"button"}
                  aria-label={type === "password" ? "Show password" : "Hide password"}
                  aria-pressed={type === "text"}
                  className={
                    "absolute top-1/2 end-2 -translate-y-1/2 cursor-pointer"
                  }
                >
                  <IconBlock name={type === "password" ? "Eye" : "EyeOff"} />
                </button>
              )}
              {prependIcon && (
                <div
                  className={
                    "absolute top-1/2 start-2 -translate-y-1/2 pointer-events-none"
                  }
                >
                  {typeof prependIcon === "string" ? (
                    <IconBlock
                      name={prependIcon as keyof typeof icons}
                      className={"text-foreground"}
                    />
                  ) : (
                    prependIcon
                  )}
                </div>
              )}
            </div>
          </FormControl>
        </FormItem>
      )}
    />
  );
}
